import { useEffect, useRef, useState } from 'react';
import { api, ErrorApi, LARGO_MINIMO_CONTRASENA } from '../api';

/**
 * Cambio de contraseña desde adentro.
 *
 * Pide la contraseña actual además de la nueva. Que haya sesión abierta no
 * prueba que quien está frente al teclado sea el dueño de la cuenta, y esa
 * verificación la hace el portal: acá solo se recoge.
 */
/** Concordancia según cuántas sesiones se cerraron. */
export function resumenDeSesiones(cerradas: number): string {
  if (cerradas === 0) return 'No había otras sesiones abiertas.';
  if (cerradas === 1) return 'Se cerró la otra sesión abierta de tu cuenta.';
  return `Se cerraron las otras ${cerradas} sesiones abiertas de tu cuenta.`;
}

export function CambiarContrasena({
  onCerrar,
  segundoFactor,
}: {
  onCerrar: () => void;
  /** Bloque de administración del segundo factor, en el mismo diálogo. */
  segundoFactor?: React.ReactNode;
}) {
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<{ sesionesCerradas: number } | null>(null);
  const dialogo = useRef<HTMLDivElement>(null);

  // Escape cierra, y el foco entra al diálogo para que se pueda usar sin mouse.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    dialogo.current?.querySelector('input')?.focus();
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  const corta = nueva.length > 0 && nueva.length < LARGO_MINIMO_CONTRASENA;
  const noCoinciden = repetida.length > 0 && nueva !== repetida;
  const repetiActual = nueva.length > 0 && nueva === actual;
  const listoParaEnviar = actual && nueva && repetida && !corta && !noCoinciden && !repetiActual;

  async function enviar(evento: React.FormEvent): Promise<void> {
    evento.preventDefault();
    if (!listoParaEnviar) return;

    setEnviando(true);
    setError(null);
    try {
      const respuesta = await api.cambiarContrasena(actual, nueva);
      setResultado({ sesionesCerradas: respuesta.sesionesCerradas });
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'no se pudo contactar el portal');
      // Solo se limpia la actual: si estaba mal, es la que hay que reescribir.
      setActual('');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="telon" onClick={onCerrar}>
      <div
        className="dialogo"
        ref={dialogo}
        role="dialog"
        aria-modal="true"
        aria-label="Seguridad de la cuenta"
        onClick={(e) => e.stopPropagation()}
      >
        {resultado ? (
          <>
            <h2>Contraseña actualizada</h2>
            <p>
              {resumenDeSesiones(resultado.sesionesCerradas)} Puedes seguir trabajando acá.
            </p>
            <div className="acciones">
              <button onClick={onCerrar}>Listo</button>
            </div>
          </>
        ) : (
          <>
            <h2>Seguridad de la cuenta</h2>
            {segundoFactor}
            <h3 className="titulo-seccion">Contraseña</h3>
            <form onSubmit={(e) => void enviar(e)}>
              <label>
                <span>Contraseña actual</span>
                <input
                  type="password"
                  value={actual}
                  onChange={(e) => setActual(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </label>

              <label>
                <span>Contraseña nueva</span>
                <input
                  type="password"
                  value={nueva}
                  onChange={(e) => setNueva(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              <label>
                <span>Repítela</span>
                <input
                  type="password"
                  value={repetida}
                  onChange={(e) => setRepetida(e.target.value)}
                  autoComplete="new-password"
                  required
                />
              </label>

              {corta && (
                <p className="aviso-campo">Te faltan {LARGO_MINIMO_CONTRASENA - nueva.length} caracteres.</p>
              )}
              {repetiActual && <p className="aviso-campo">Tiene que ser distinta de la actual.</p>}
              {noCoinciden && <p className="aviso-campo">Las dos contraseñas no coinciden.</p>}
              {error && <p className="error">{error}</p>}

              <div className="acciones">
                <button type="button" className="secundario" onClick={onCerrar}>
                  Cancelar
                </button>
                <button type="submit" disabled={enviando || !listoParaEnviar}>
                  {enviando ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
